import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import AdminTable from "components/admin/AdminTable";
import { useFetch } from "components/admin/hooks";
import api from "api/client";
import { boardName, parseJson, scoreKeys, showTime } from "./shared";

/** Top 200 của một bảng điểm trong mùa, đọc thẳng event_score (đã ghi DB, có thể trễ tối đa 30 giây). */
function RankingDialog({ season, onClose }) {
  const config = useMemo(() => parseJson(season.config, {}), [season.config]);
  const keys = useMemo(() => scoreKeys(config), [config]);
  const [key, setKey] = useState(keys[0] || "");
  const ranking = useFetch(
    () =>
      key ? api.get(`/api/admin/event/seasons/${season.id}/ranking`, { key }) : Promise.resolve([]),
    [season.id, key]
  );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Bảng điểm · {season.name} (#{season.id})
      </DialogTitle>
      <DialogContent>
        <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
          <MDBox display="flex" gap={1} alignItems="center">
            <MDInput
              select
              label="Bảng"
              value={key}
              sx={{ minWidth: 260 }}
              onChange={(e) => setKey(e.target.value)}
            >
              {keys.map((k) => (
                <MenuItem key={k} value={k}>
                  {boardName(config, k)} ({k})
                </MenuItem>
              ))}
            </MDInput>
            <MDButton size="small" color="info" variant="outlined" onClick={() => ranking.reload()}>
              Tải lại
            </MDButton>
          </MDBox>
          {ranking.error && <Alert severity="error">{ranking.error}</Alert>}
          <AdminTable
            loading={ranking.loading}
            rows={(ranking.data || []).map((row, index) => ({ ...row, rank: index + 1 }))}
            rowKey="userId"
            emptyText="Chưa có điểm"
            columns={[
              { key: "rank", label: "Hạng" },
              {
                key: "name",
                label: "Nhân vật",
                render: (row) => (
                  <Link to={`/players/${row.userId}`}>{row.name || `Tài khoản ${row.userId}`}</Link>
                ),
              },
              {
                key: "value",
                label: "Điểm",
                align: "right",
                render: (row) => Number(row.value).toLocaleString("vi-VN"),
              },
              { key: "reachedAt", label: "Đạt lúc", render: (row) => showTime(row.reachedAt) },
            ]}
          />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>
          Đóng
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

RankingDialog.propTypes = {
  season: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    config: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RankingDialog;
